import React, { useCallback, useEffect, useMemo, useState } from "react";
import { noop, omit } from "lodash";
import { Form, FormSpy } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { PopoverPosition } from "@blueprintjs/core";
import classNames from "classnames";
import Dialog, { CELL_MODEL_DEPENDENCY_BAR_MAX_HEIGHT } from "components/Dialog";
import SimpleRemoveButton from "components/SimpleRemoveButton";
import DynamicFields from "../DynamicFields";
import DependencyGraph from "./components/DependencyGraph";
import TextTruncate from "components/TextTruncate";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import Button, { ButtonStyle } from "components/Button";
import LearnMoreLink from "rs-components/LearnMoreLink";
import InfoText from "./components/InfoText";
import TypeDescription from "./components/TypeDescription";
import ScrollBar from "components/ScrollBar";
import { addVariableDependency, deleteVariableDependency, getVariableDependencyGraphData, setVariableDependencyGraphData, updateVariableDependency, } from "store/cell-model/cell-model.actions";
import { cellModelDependencyDialogStatusSelector, dynamicByIdSelector, dynamicDependencySelector, isRefinedSelector, variableNodeByIdSelector, } from "store/cell-model/cell-model.selectors";
import { DependencyPropKeys, DependencyRestrictionKeys, DependencyType, DynamicPropKeys, DynamicType, } from "store/cell-model/cell-model.types";
import { getNumberFromNumberString } from "utils/string";
import { ACCEPTABLE_ERROR_MESSAGE, isNotEmpty } from "utils/validate";
import { isEditableSelector, restrictionsSelector, serverErrorsSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { RecipeScalingApiEndpoints } from "api/api-endpoints";
import { GetRestrictionsMethod, RemovableEntityType, RequestTemplate, } from "store/knowledge-base-item/knowledge-base-item.types";
import { getRestrictions, setDeleteTarget, setServerError, } from "store/knowledge-base-item/knowledge-base-item.actions";
import { isDependencyDataConfigured } from "store/cell-model/cell-model.utils";
import { DIALOG_ACTION_TITLE, INCORRECT_INPUTS_MESSAGE } from "shared/labels";
import { isDialogInPending, isDialogOpened, isFormChanged, isFormValid } from "shared/validators";
import { HELP_SYSTEM_URL, HelpId } from "shared/helpSystemBindings";
import QuestionMarkIcon from "icons/questionmark.svg";
import QuestionMarkHoverIcon from "icons/questionmark_hover.svg";
import * as styles from "./styles.module.scss";
const DependencyTypeLabel = {
    [DependencyType.Range]: "Within range",
    [DependencyType.OutOfRange]: "Outside of range",
    [DependencyType.Distribution]: "Has an optimum",
    [DependencyType.AsymmetricalDistribution]: "Has an optimum (asymmetrical distribution)",
    [DependencyType.NegativeDistribution]: "Has a low point",
    [DependencyType.NegativeAsymmetricalDistribution]: "Has a low point (asymmetrical distribution)",
    [DependencyType.Sigmoidal]: "Positive effect with threshold",
    [DependencyType.NegativeSigmoidal]: "Negative effect with threshold",
    [DependencyType.Saturating]: "Positive effect without threshold",
    [DependencyType.NegativeSaturating]: "Negative effect without threshold",
};
const DYNAMIC_LABELS = {
    type: "Uncertainty",
    mean: "Value for maximum rate",
    sd: "Spread",
    from: "Lowest acceptable",
    to: "Highest acceptable",
    value: "Known Value",
};
const TYPE_ITEMS = Object.values(DependencyType).map((value) => ({
    label: DependencyTypeLabel[value],
    value,
}));
export const DependencyVariablesByType = {
    [DependencyType.Range]: [
        {
            label: "Lowest acceptable",
            prefix: "Min",
            name: DependencyPropKeys.from,
        },
        {
            label: "Highest acceptable",
            prefix: "Max",
            name: DependencyPropKeys.to,
        },
    ],
    [DependencyType.OutOfRange]: [
        {
            label: "Start of range",
            prefix: "Start",
            name: DependencyPropKeys.from,
        },
        {
            label: "End of range",
            prefix: "End",
            name: DependencyPropKeys.to,
        },
    ],
    [DependencyType.Distribution]: [
        {
            label: "Value for maximum rate",
            prefix: "Max rate",
            name: DependencyPropKeys.mean,
        },
        {
            label: "Spread",
            prefix: "Spread",
            name: DependencyPropKeys.sd,
        },
    ],
    [DependencyType.AsymmetricalDistribution]: [
        {
            label: "Value for maximum rate",
            prefix: "Max rate",
            name: DependencyPropKeys.mean,
        },
        {
            label: "Left spread",
            prefix: "Left spread",
            name: DependencyPropKeys.leftSD,
        },
        {
            label: "Right spread",
            prefix: "Right spread",
            name: DependencyPropKeys.rightSD,
        },
    ],
    [DependencyType.NegativeDistribution]: [
        {
            label: "Value for zero rate",
            prefix: "Zero rate",
            name: DependencyPropKeys.mean,
        },
        {
            label: "Spread",
            prefix: "Spread",
            name: DependencyPropKeys.sd,
        },
    ],
    [DependencyType.NegativeAsymmetricalDistribution]: [
        {
            label: "Value for zero rate",
            prefix: "Max rate",
            name: DependencyPropKeys.mean,
        },
        {
            label: "Left spread",
            prefix: "Left spread",
            name: DependencyPropKeys.leftSD,
        },
        {
            label: "Right spread",
            prefix: "Right spread",
            name: DependencyPropKeys.rightSD,
        },
    ],
    [DependencyType.Sigmoidal]: [
        {
            label: "50% of maximum rate",
            prefix: "50% of Max rate",
            name: DependencyPropKeys.percent50,
        },
        {
            label: "95% of maximum rate",
            prefix: "95% of Max rate",
            name: DependencyPropKeys.percent95,
        },
    ],
    [DependencyType.NegativeSigmoidal]: [
        {
            label: "50% of maximum rate",
            prefix: "50% of Max rate",
            name: DependencyPropKeys.percent50,
        },
        {
            label: "5% of maximum rate",
            prefix: "5% of Max rate",
            name: DependencyPropKeys.percent05,
        },
    ],
    [DependencyType.Saturating]: [
        {
            label: "50% of maximum rate",
            prefix: "50% of Max rate",
            name: DependencyPropKeys.halfEffect,
        },
    ],
    [DependencyType.NegativeSaturating]: [
        {
            label: "50% of maximum rate",
            prefix: "50% of Max rate",
            name: DependencyPropKeys.halfEffect,
        },
    ],
};
const getRestrictionsPayload = (post) => ({
    rsEndpoint: RecipeScalingApiEndpoints.cellModels,
    template: post ? RequestTemplate.VARIABLE_DEPENDENCIES_POST : RequestTemplate.VARIABLE_DEPENDENCIES_PUT,
    method: post ? GetRestrictionsMethod.POST : GetRestrictionsMethod.PUT,
});
const prepareDependencyProperties = (dependencyProperties) => {
    const { type: dependencyType, ...values } = dependencyProperties;
    return Object.entries(values).reduce((parsedDependency, entry) => {
        const [dependencyKey, dependencyValue] = entry;
        if (dependencyKey === dependencyType || !dependencyValue) {
            return parsedDependency;
        }
        const { type: dynamicType, ...restDependencyValue } = dependencyValue;
        return {
            ...parsedDependency,
            [dependencyKey]: Object.entries(restDependencyValue).reduce((parsedDynamic, [dynamicKey, dynamicValue]) => ({
                ...parsedDynamic,
                [dynamicKey]: getNumberFromNumberString(`${dynamicValue}`, false),
            }), { type: dynamicType }),
        };
    }, dependencyProperties);
};
const DependencyVariableDialog = ({ dynamicId, dependencyId = null, variableId, handleClose = noop, }) => {
    const [infoVisible, setInfoVisible] = useState(false);
    const { id: cellModelId } = useParams();
    const dispatch = useDispatch();
    const dependency = useSelector(dynamicDependencySelector(dependencyId, dynamicId));
    const dynamicNode = useSelector(dynamicByIdSelector(dynamicId));
    const variableNode = useSelector(variableNodeByIdSelector(variableId));
    const restrictions = useSelector(restrictionsSelector(getRestrictionsPayload(dependencyId === null)));
    const dialogStatus = useSelector(cellModelDependencyDialogStatusSelector);
    const editable = useSelector(isEditableSelector);
    const refined = useSelector(isRefinedSelector);
    const serverErrors = useSelector(serverErrorsSelector(dependencyId === null
        ? RequestTemplate.VARIABLE_DEPENDENCIES_POST
        : RequestTemplate.VARIABLE_DEPENDENCIES_PUT));
    const graphServerErrors = useSelector(serverErrorsSelector(RequestTemplate.VARIABLE_DEPENDENCIES_GRAPH_GET));
    useEffect(() => {
        !restrictions && dispatch(getRestrictions(getRestrictionsPayload(dependencyId === null)));
    }, [restrictions, dispatch, dependencyId]);
    const dependencyPropertiesRestrictions = useMemo(() => restrictions?.["Request.DependencyProperties"], [restrictions]);
    const getGraphData = useCallback(({ values, active }) => {
        if (!!active || !values.type) {
            return;
        }
        const dependencyProperties = prepareDependencyProperties(values);
        if (isNotEmpty(cellModelId) && isNotEmpty(dynamicId)) {
            const allFieldsCompleted = DependencyVariablesByType[values.type].every(({ name }) => {
                const fieldValue = dependencyProperties[name] ?? {};
                switch (fieldValue.type) {
                    case DynamicType.FIXED:
                        return isNotEmpty(fieldValue[DynamicPropKeys.value]);
                    case DynamicType.DISTRIBUTION:
                        return (isNotEmpty(fieldValue[DynamicPropKeys.mean]) && isNotEmpty(fieldValue[DynamicPropKeys.sd]));
                    case DynamicType.RANGE:
                        return (isNotEmpty(fieldValue[DynamicPropKeys.from]) && isNotEmpty(fieldValue[DynamicPropKeys.to]));
                    default:
                        return false;
                }
            });
            if (allFieldsCompleted) {
                const getGraphDataPayload = {
                    cellModelId: Number(cellModelId),
                    dynamicId,
                    dependencyProperties: prepareDependencyProperties(values),
                };
                dispatch(getVariableDependencyGraphData(getGraphDataPayload));
            }
            else {
                dispatch(setVariableDependencyGraphData(null));
            }
        }
        else {
            dispatch(setVariableDependencyGraphData(null));
        }
    }, [cellModelId, dispatch, dynamicId]);
    useEffect(() => () => {
        dispatch(setVariableDependencyGraphData(null));
    }, [dispatch]);
    const onSubmit = useCallback((values) => {
        if (cellModelId !== null) {
            const addPayload = {
                cellModelId: Number(cellModelId),
                variableId,
                dynamicId,
                dependencyProperties: prepareDependencyProperties(values),
            };
            if (dependencyId) {
                dispatch(updateVariableDependency({ ...addPayload, dependencyId: dependencyId }));
            }
            else {
                dispatch(addVariableDependency(addPayload));
            }
        }
    }, [cellModelId, dependencyId, dispatch, dynamicId, variableId]);
    const resetServerErrors = useCallback(() => (isNotEmpty(serverErrors) || isNotEmpty(graphServerErrors)) && dispatch(setServerError(null)), [dispatch, graphServerErrors, serverErrors]);
    const onTypeChange = useCallback((type, form) => {
        form.reset({ type: type.value });
    }, []);
    const openRemoveConfirmationModal = useCallback(() => dependencyId !== null &&
        dispatch(setDeleteTarget({
            id: dependencyId,
            type: RemovableEntityType.DEPENDENCY,
        })), [dependencyId, dispatch]);
    const onDeleteDependency = useCallback((isConfirmed) => isConfirmed ? dispatch(deleteVariableDependency(dynamicId)) : dispatch(setDeleteTarget(null)), [dispatch, dynamicId]);
    return (React.createElement(Form, { onSubmit: onSubmit, validateOnBlur: true, initialValues: omit(dependency?.dependencyProperties ?? {}, "isCompleted"), render: ({ handleSubmit, form, values, ...renderProps }) => (React.createElement(Dialog, { isOpen: isDialogOpened(dialogStatus), title: React.createElement("div", { className: styles.title },
                React.createElement("div", { className: styles["title-dynamicName"] },
                    React.createElement(TextTruncate, { text: dynamicNode?.name }),
                    React.createElement("span", null, " dependency on")),
                React.createElement(TextTruncate, { text: variableNode?.name })), className: styles.dialog, primaryButton: editable && !refined
                ? {
                    label: DIALOG_ACTION_TITLE.OK,
                    onClick: isFormChanged(renderProps, dependencyId) ? handleSubmit : handleClose,
                    disabled: !isFormValid(renderProps) || isDialogInPending(dialogStatus),
                    title: !renderProps.valid ? INCORRECT_INPUTS_MESSAGE : "",
                }
                : { label: DIALOG_ACTION_TITLE.CLOSE, onClick: handleClose }, secondaryButtons: editable && !refined ? [{ label: DIALOG_ACTION_TITLE.CANCEL, onClick: handleClose }] : [], onClosing: async () => {
                await form.reset();
                resetServerErrors();
                handleClose();
            } },
            React.createElement(FormSpy, { onChange: getGraphData, subscription: { values: true, valid: true, active: true } }),
            React.createElement(ScrollBar, { autoHeight: true, autoHeightMax: CELL_MODEL_DEPENDENCY_BAR_MAX_HEIGHT },
                React.createElement("form", { className: styles.form, onSubmit: (e) => e.preventDefault() },
                    React.createElement("div", { className: classNames(styles.typeBlock) },
                        React.createElement("div", { className: styles.learnMoreBlock },
                            React.createElement(LearnMoreLink, { className: styles.linkBlock, url: `${HELP_SYSTEM_URL}#cshid=${HelpId.DYNAMIC_DEPENDENCY}` }),
                            React.createElement(Input, { component: SelectInput, form: form, name: DependencyPropKeys.type, autoFocus: true, onChange: (value) => onTypeChange(value, form), itemRenderer: SelectItemRenderer, disableFormChange: true, label: "Type", placeholder: "Select", value: TYPE_ITEMS.find((item) => item.value === values.type), items: TYPE_ITEMS, readonly: !editable || refined, classes: {
                                    container: styles.select,
                                    component: styles.selectWrapper,
                                    label: styles.label,
                                }, restrictions: {
                                    IsMandatory: true,
                                } })),
                        editable && values.type && !refined && (React.createElement(Button, { title: "Info", position: PopoverPosition.RIGHT, square: true, style: ButtonStyle.Link, onClick: () => setInfoVisible(!infoVisible), label: infoVisible ? (React.createElement(QuestionMarkHoverIcon, { className: styles.questionIcon, alt: "read more about dependency types" })) : (React.createElement(QuestionMarkIcon, { className: styles.questionIcon, alt: "read more about dependency types" })), classes: { root: styles.question } }))),
                    React.createElement("div", { className: styles.relative },
                        React.createElement(InfoText, { className: classNames({
                                [styles.hidden]: values.type && !infoVisible,
                            }) }),
                        React.createElement("div", { className: classNames(styles.flex, styles.content, {
                                [styles.hidden]: infoVisible || !values.type,
                            }) },
                            React.createElement("div", { className: styles.leftSide },
                                values.type && React.createElement(TypeDescription, { type: values.type }),
                                values.type &&
                                    DependencyVariablesByType[values.type].map(({ name, label }) => (React.createElement("div", { className: styles.row, key: name },
                                        React.createElement("div", { className: styles.subTitle }, label),
                                        React.createElement(DynamicFields, { name: name, restrictions: dependencyPropertiesRestrictions?.[values.type]?.[DependencyRestrictionKeys[name]], dynamicLabels: DYNAMIC_LABELS, editable: editable && !refined, serverErrors: serverErrors ?? graphServerErrors, customErrorMessage: ACCEPTABLE_ERROR_MESSAGE, serverErrorsName: graphServerErrors
                                                ? DependencyRestrictionKeys[name]
                                                : `Request.DependencyProperties.${DependencyRestrictionKeys[name]}`, resetServerErrors: resetServerErrors }))))),
                            isDependencyDataConfigured(values) && (React.createElement(DependencyGraph, { dynamicName: dynamicNode?.name, variableName: variableNode?.name, units: variableNode?.unit, className: styles.rightSide })))))),
            editable && dependencyId !== null && !refined && (React.createElement("div", { className: styles["delete-button"] },
                React.createElement(SimpleRemoveButton, { onOpen: openRemoveConfirmationModal, action: onDeleteDependency, isMenuItem: false }))))) }));
};
export default DependencyVariableDialog;
